import urls from "../../Utils/urls";

/**
 * Build a usable <img src> from whatever the blog API hands back.
 *
 * BlogsController::buildBlogImageUrl() is supposed to return a bare filename,
 * but older rows still hold full S3 URLs or an "upload/" prefix, and the
 * literal string "DIRECT_DOC_URL" leaked into some records when the PHP
 * constant was concatenated before it was defined. Normalise all of those.
 */
export const blogImageUrl = (raw) => {
  if (!raw || typeof raw !== "string") return "";

  let file = raw.trim();
  if (!file) return "";

  // Already absolute -- use as-is.
  if (/^https?:\/\//i.test(file)) return file;

  file = file
    .replace(/^DIRECT_DOC_URL\/?/, "")
    .replace(/^WEBSITE_DOCUMENT_BASE_URL\/?/, "")
    .replace(/^\/?upload\//i, "")
    .replace(/^\/+/, "");

  if (!file) return "";

  return urls.BASE_IMAGE_URL + file;
};

/** "2026-09-15 16:20:47" / "2026-12-09" -> "15 Sep 2026" */
export const formatBlogDate = (value) => {
  if (!value) return "";
  // Safari chokes on "YYYY-MM-DD HH:mm:ss"; the ISO form parses everywhere.
  const parsed = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/** Rough minutes-to-read from an HTML body, at 200 wpm. */
export const readingTime = (html) => {
  if (!html) return 1;
  const text = String(html).replace(/<[^>]*>/g, " ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
};

/** Plain-text excerpt, trimmed on a word boundary. */
export const excerpt = (value, max = 150) => {
  if (!value) return "";
  const text = String(value)
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (text.length <= max) return text;
  return text.slice(0, text.lastIndexOf(" ", max)).trim() + "…";
};

const slugifyHeading = (text, index) => {
  const base = text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .slice(0, 60);
  return base ? `${base}-${index}` : `section-${index}`;
};

const ALLOWED_ATTRS = ["href", "src", "alt", "title", "colspan", "rowspan"];

// A <br>-only or &nbsp;-only node is spacing, not content -- Google Docs emits
// plenty of both. Only real media keeps an otherwise-textless node alive.
const isBlank = (el) =>
  !el.querySelector("img, iframe") &&
  el.textContent.replace(/[\s ]/g, "") === "";

/**
 * The CRM editor stores Google-Docs paste: every paragraph is a <p> wrapping a
 * <span> with inline "Times New Roman" / pt sizes, and headings are just bold
 * large spans rather than real <h2>/<h3>. Inline styles beat any stylesheet, so
 * rather than fighting them with !important we rebuild the markup: read the
 * inline sizes once to recover the heading hierarchy, then drop the styles.
 *
 * Returns { html, headings } -- headings feed the on-page table of contents.
 */
export const normalizeBlogHtml = (rawHtml, { dropTitle = "" } = {}) => {
  if (!rawHtml) return { html: "", headings: [] };

  // No DOMParser (SSR / older jsdom) -- fall back to the raw markup.
  if (typeof window === "undefined" || typeof window.DOMParser === "undefined") {
    return { html: rawHtml, headings: [] };
  }

  const doc = new window.DOMParser().parseFromString(
    `<div id="su-blog-root">${rawHtml}</div>`,
    "text/html"
  );
  const root = doc.getElementById("su-blog-root");
  if (!root) return { html: rawHtml, headings: [] };

  root.querySelectorAll("style, script, meta, link").forEach((n) => n.remove());

  // -- Pass 1: recover heading level from the inline styles, before we strip them.
  const promote = [];
  root.querySelectorAll("p, div").forEach((el) => {
    if (el.querySelector("p, div, ul, ol, table, img")) return;
    if (isBlank(el)) return;

    const styled = el.matches("[style]") ? [el] : [];
    const spans = Array.from(el.querySelectorAll("span, b, strong"));
    const candidates = styled.concat(spans);
    if (!candidates.length) return;

    let maxPt = 0;
    let bold = false;
    candidates.forEach((node) => {
      const style = node.getAttribute("style") || "";
      const size = /font-size:\s*([\d.]+)\s*(pt|px)/i.exec(style);
      if (size) {
        const value = parseFloat(size[1]);
        maxPt = Math.max(maxPt, size[2].toLowerCase() === "px" ? value * 0.75 : value);
      }
      if (
        /font-weight:\s*(700|800|900|bold)/i.test(style) ||
        node.tagName === "B" ||
        node.tagName === "STRONG"
      ) {
        bold = true;
      }
    });

    // Body copy sits at 11-12pt; anything bold and noticeably larger is a heading.
    if (bold && maxPt >= 15 && el.textContent.trim().length < 160) {
      promote.push({ el, level: maxPt >= 21 ? 2 : 3 });
    }
  });

  promote.forEach(({ el, level }) => {
    const heading = doc.createElement(`h${level}`);
    heading.innerHTML = el.innerHTML;
    el.replaceWith(heading);
  });

  // Any authored h1 becomes an h2 -- the page title owns the only h1.
  root.querySelectorAll("h1").forEach((el) => {
    const heading = doc.createElement("h2");
    heading.innerHTML = el.innerHTML;
    el.replaceWith(heading);
  });

  // -- Pass 2: strip presentation, keep structure.
  root.querySelectorAll("*").forEach((el) => {
    Array.from(el.attributes).forEach((attr) => {
      if (!ALLOWED_ATTRS.includes(attr.name.toLowerCase())) {
        el.removeAttribute(attr.name);
      }
    });
  });

  // Spans carry nothing once the styles are gone -- unwrap them.
  root.querySelectorAll("span, font").forEach((el) => {
    el.replaceWith(...Array.from(el.childNodes));
  });

  root.normalize();

  // -- Pass 3: links, empties, headings/ids.
  root.querySelectorAll("a").forEach((el) => {
    const href = el.getAttribute("href") || "";
    if (/^\s*javascript:/i.test(href)) {
      el.removeAttribute("href");
      return;
    }
    if (/^https?:\/\//i.test(href) && !href.includes("salaryuncle.com")) {
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
    }
  });

  root.querySelectorAll("p, div").forEach((el) => {
    if (isBlank(el)) el.remove();
  });

  // Titles are usually "Real Title | Brand suffix"; only the part before the
  // pipe is repeated as the body's opening heading.
  const normalisedTitle = dropTitle
    .split("|")[0]
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "")
    .slice(0, 30);

  const headings = [];
  Array.from(root.querySelectorAll("h2, h3")).forEach((el, index) => {
    const text = el.textContent.trim();
    if (!text) {
      el.remove();
      return;
    }

    // The pasted body usually repeats the article title as its first heading.
    if (
      index === 0 &&
      normalisedTitle &&
      text.toLowerCase().replace(/[^a-z0-9]/g, "").startsWith(normalisedTitle)
    ) {
      el.remove();
      return;
    }

    const id = slugifyHeading(text, index);
    el.setAttribute("id", id);
    headings.push({ id, el, text, level: el.tagName === "H2" ? 2 : 3 });
  });

  // Dropping the repeated title can leave a document made entirely of h3s.
  // Flatten that back to h2 so the page has a real top-level structure.
  if (headings.length && headings.every((item) => item.level === 3)) {
    headings.forEach((item) => {
      const heading = doc.createElement("h2");
      heading.innerHTML = item.el.innerHTML;
      heading.setAttribute("id", item.id);
      item.el.replaceWith(heading);
      item.level = 2;
    });
  }

  return {
    html: root.innerHTML,
    headings: headings.map(({ id, text, level }) => ({ id, text, level })),
  };
};
